import React, { useState, useEffect, useRef } from 'react';

const API = '';

function StarRating({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display:'flex', gap:6, marginBottom:8 }}>
      {[1,2,3,4,5].map(n => (
        <button key={n}
          onClick={() => onChange(n)}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          style={{ background:'none', border:'none', cursor:'pointer', fontSize:32,
            color: n <= (hover||value) ? '#f59e0b' : '#d1d5db' }}>★</button>
      ))}
    </div>
  );
}

export default function FeedbackViewPage() {
  const token = window.location.pathname.split('/feedback/')[1];
  const [item, setItem] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Ensure page body is always scrollable
  useEffect(() => {
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);
  const [passwordMode, setPasswordMode] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [verified, setVerified] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [responses, setResponses] = useState([]);
  const [showResponses, setShowResponses] = useState(false);
  const [form, setForm] = useState({ score: 0, feedback_text: '', video_timestamp: null });
  const [attachedFile, setAttachedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [videoTimestamp, setVideoTimestamp] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approvalModalText, setApprovalModalText] = useState('By clicking Approve you confirm you are happy with this content and no further changes are required. This approval is final.');
  const pingRef = useRef(null);
  const startRef = useRef(Date.now());

  useEffect(() => {
    // Fetch configurable approval modal text
    fetch(`${API}/api/feedback/approval-text`)
      .then(r => r.json()).then(d => { if (d.text) setApprovalModalText(d.text); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!token) { setError('Invalid link'); setLoading(false); return; }
    fetch(`${API}/api/feedback/view/${token}`)
      .then(r => r.json()).then(data => {
        if (data.error) { setError(data.error); setLoading(false); return; }
        setItem(data);
        setLoading(false);
        if (data.password_protected) { setPasswordMode(true); }
        else { setVerified(true); }
      }).catch(() => { setError('Failed to load'); setLoading(false); });
  }, [token]);

  // Start view session ping on verify
  useEffect(() => {
    if (!verified || !token) return;
    fetch(`${API}/api/feedback/view/${token}/ping`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ event:'start' }) });
    startRef.current = Date.now();
    pingRef.current = setInterval(() => {
      const secs = Math.round((Date.now() - startRef.current) / 1000);
      fetch(`${API}/api/feedback/view/${token}/ping`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ event:'ping', duration_seconds: secs }) });
    }, 30000);
    const onEnd = () => {
      const secs = Math.round((Date.now() - startRef.current) / 1000);
      fetch(`${API}/api/feedback/view/${token}/ping`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ event:'end', duration_seconds: secs }), keepalive:true });
    };
    window.addEventListener('beforeunload', onEnd);
    return () => { clearInterval(pingRef.current); window.removeEventListener('beforeunload', onEnd); };
  }, [verified, token]);

  // YouTube/Vimeo message listener for video timestamp on pause
  useEffect(() => {
    const onMsg = (e) => {
      try {
        const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
        // YouTube
        if (data.event === 'infoDelivery' && data.info?.currentTime !== undefined) {
          setVideoTimestamp(Math.floor(data.info.currentTime));
        }
        // Vimeo pause event
        if (data.event === 'pause' && data.data?.seconds !== undefined) {
          const ts = Math.floor(data.data.seconds);
          setVideoTimestamp(ts);
          setForm(prev => ({
            ...prev,
            video_timestamp: ts,
            feedback_text: prev.feedback_text
              ? prev.feedback_text + `
[${fmtTime(ts)}] `
              : `[${fmtTime(ts)}] `
          }));
        }
        // YouTube state change: 2 = paused
        if (data.info === 2 && data.event === 'infoDelivery') {
          // handled by currentTime below
        }
        if (data.event === 'infoDelivery' && data.info?.playerState === 2 && data.info?.currentTime !== undefined) {
          const ts = Math.floor(data.info.currentTime);
          setVideoTimestamp(ts);
        }
      } catch {}
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  const fmtTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2,'0')}`;
  };

  const handleVerify = async () => {
    const r = await fetch(`${API}/api/feedback/view/${token}/verify`, {
      method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ password })
    });
    const { ok } = await r.json();
    if (ok) { setVerified(true); setPasswordMode(false); setPasswordError(''); }
    else setPasswordError('Incorrect password');
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      let file_id = null;
      if (attachedFile) {
        setUploadingFile(true);
        try {
          const fd = new FormData();
          fd.append('file', attachedFile);
          fd.append('folder', 'documents');
          fd.append('entity_type', 'feedback');
          fd.append('entity_id', '0');
          const r = await fetch(`${API}/api/files/public-upload`, { method:'POST', body: fd });
          const fData = await r.json();
          file_id = fData.id || null;
        } catch(e) {}
        setUploadingFile(false);
      }
      await fetch(`${API}/api/feedback/view/${token}/respond`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({
          ...form,
          video_timestamp: form.video_timestamp || videoTimestamp || null,
          file_id,
          is_approval: false,
        })
      });
      setSubmitted(true);
    } catch(e) {}
    setSubmitting(false);
  };

  const handleApprove = async () => {
    setShowApproveModal(false);
    setSubmitting(true);
    try {
      await fetch(`${API}/api/feedback/view/${token}/respond`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ is_approval: true, feedback_text: '', score: null })
      });
      setSubmitted(true);
    } catch(e) {}
    setSubmitting(false);
  };

  const loadResponses = async () => {
    const r = await fetch(`${API}/api/feedback/view/${token}/responses`);
    const data = await r.json();
    setResponses(data); setShowResponses(true);
  };

  const feedbackTypes = (() => { try { return JSON.parse(item?.feedback_types || '["text"]'); } catch { return ['text']; } })();

  const embedUrl = () => {
    if (!item?.content_url) return null;
    const url = item.content_url;
    // YouTube
    const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{11})/);
    if (yt) return `https://www.youtube.com/embed/${yt[1]}?enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`;
    // Vimeo
    const vi = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vi) return `https://player.vimeo.com/video/${vi[1]}?autopause=1`;
    return null;
  };

  // ── Styles ──
  const bg = 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)';
  const card = { background:'rgba(255,255,255,0.05)', backdropFilter:'blur(20px)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:16, padding:28 };
  const btn = { background:'linear-gradient(135deg, #EE7203 0%, #f59e0b 100%)', color:'white', border:'none', borderRadius:10, padding:'12px 28px', fontSize:15, fontWeight:700, cursor:'pointer', width:'100%', marginTop:8 };
  const inp = { background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.2)', borderRadius:8, color:'white', padding:'10px 14px', width:'100%', fontSize:14, outline:'none', fontFamily:'inherit' };

  if (loading) return (
    <div style={{ minHeight:'100vh', background:bg, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ color:'rgba(255,255,255,0.6)', fontSize:16 }}>Loading…</div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight:'100vh', background:bg, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ ...card, textAlign:'center', maxWidth:400 }}>
        <div style={{ fontSize:48, marginBottom:12 }}>🔍</div>
        <div style={{ color:'white', fontSize:18, fontWeight:600 }}>Link not found</div>
        <div style={{ color:'rgba(255,255,255,0.5)', marginTop:8 }}>This feedback link may have expired or been removed.</div>
      </div>
    </div>
  );

  if (passwordMode) return (
    <div style={{ minHeight:'100vh', background:bg, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ ...card, maxWidth:400, width:'100%' }}>
        <div style={{ textAlign:'center', marginBottom:20 }}>
          <img src="/forgebos-logo-white.png" alt="ForgeBOS" style={{ height:44, marginBottom:12 }} />
          <div style={{ color:'rgba(255,255,255,0.5)', fontSize:12 }}>Feedback Preview</div>
        </div>
        <div style={{ color:'white', fontWeight:600, marginBottom:6, fontSize:15 }}>🔒 Password required</div>
        <input style={inp} type="password" placeholder="Enter password" value={password}
          onChange={e => setPassword(e.target.value)}
          onKeyDown={e => e.key==='Enter' && handleVerify()} />
        {passwordError && <div style={{ color:'#f87171', fontSize:13, marginTop:6 }}>{passwordError}</div>}
        <button style={btn} onClick={handleVerify}>Access Feedback</button>
      </div>
    </div>
  );

  if (item?.is_archived) return (
    <div style={{ minHeight:'100vh', background:bg, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ ...card, textAlign:'center', maxWidth:400 }}>
        <div style={{ fontSize:48, marginBottom:12 }}>📦</div>
        <div style={{ color:'white', fontSize:18, fontWeight:600 }}>This version has been archived</div>
        <div style={{ color:'rgba(255,255,255,0.5)', marginTop:8 }}>A new version may be available. Please contact the team.</div>
      </div>
    </div>
  );

  const embed = embedUrl();

  return (
    <div style={{ minHeight:'100vh', background:bg, padding:'32px 16px', fontFamily:'-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', overflowY:'auto' }}>
      <div style={{ maxWidth:860, margin:'0 auto' }}>

        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:28 }}>
          <a href="https://www.ForgeBOS.com" target="_blank" rel="noopener noreferrer" style={{ display:'inline-block' }}>
            <img src="/forgebos-logo-white.png" alt="ForgeBOS" style={{ height:34, cursor:'pointer' }} />
          </a>
          <div style={{ color:'rgba(255,255,255,0.4)', fontSize:12 }}>Feedback Preview</div>
        </div>

        {/* Title card */}
        <div style={{ ...card, marginBottom:20 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
            <div>
              <h1 style={{ color:'white', fontSize:24, fontWeight:800, margin:'0 0 6px' }}>{item.title}</h1>
              {item.version_number && (
                <span style={{ background:'rgba(238,114,3,0.2)', border:'1px solid rgba(238,114,3,0.4)', borderRadius:6, color:'#f59e0b', fontSize:12, padding:'2px 10px', fontWeight:600 }}>
                  Version {item.version_number}
                </span>
              )}
            </div>
            {item.contact_label && <div style={{ color:'rgba(255,255,255,0.5)', fontSize:13 }}>For: {item.contact_label}</div>}
          </div>
          {item.description && <p style={{ color:'rgba(255,255,255,0.7)', marginTop:14, lineHeight:1.6, fontSize:14 }}>{item.description}</p>}
          {item.status_update && (
            <div style={{ marginTop:14, background:'rgba(99,102,241,0.15)', border:'1px solid rgba(99,102,241,0.3)', borderRadius:10, padding:'12px 16px' }}>
              <div style={{ color:'#a5b4fc', fontSize:11, fontWeight:700, marginBottom:4, textTransform:'uppercase', letterSpacing:'0.05em' }}>📢 Status Update</div>
              <div style={{ color:'white', fontSize:14, lineHeight:1.6 }}>{item.status_update}</div>
            </div>
          )}
        </div>

        {/* Content area */}
        {embed && (
          <div style={{ ...card, marginBottom:20, padding:0, overflow:'hidden', borderRadius:16 }}>
            <div style={{ position:'relative', width:'100%', paddingBottom:'56.25%' }}>
            <iframe src={embed} style={{ width:'100%', height:'100%', border:'none', position:'absolute', top:0, left:0 }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen title="Content" />
            </div>
          </div>
        )}

        {!embed && item.content_type === 'link' && item.content_url && (
          <div style={{ ...card, marginBottom:20, textAlign:'center' }}>
            <div style={{ fontSize:40, marginBottom:12 }}>🔗</div>
            <div style={{ color:'white', fontWeight:600, marginBottom:10 }}>External Link</div>
            <a href={item.content_url} target="_blank" rel="noopener noreferrer"
              style={{ ...btn, display:'inline-block', textDecoration:'none', width:'auto', padding:'10px 24px' }}>
              Open Link ↗
            </a>
          </div>
        )}

        {!embed && item.stored_name && item.folder && (
          <div style={{ ...card, marginBottom:20 }}>
            {item.content_type === 'image' ? (
              <img src={`/api/files/${item.folder}/${item.stored_name}`} alt={item.file_name}
                style={{ maxWidth:'100%', borderRadius:8, display:'block', margin:'0 auto' }} />
            ) : item.mime_type === 'application/pdf' ? (
              <div>
                <div style={{ color:'rgba(255,255,255,0.6)', fontSize:13, marginBottom:10 }}>📄 {item.file_name}</div>
                <iframe src={`/api/files/${item.folder}/${item.stored_name}`}
                  style={{ width:'100%', height:600, borderRadius:8, border:'none' }} title="Document" />
              </div>
            ) : (
              <div style={{ textAlign:'center' }}>
                <div style={{ fontSize:40, marginBottom:10 }}>📄</div>
                <div style={{ color:'white', fontWeight:600, marginBottom:12 }}>{item.file_name}</div>
                <a href={`/api/files/${item.folder}/${item.stored_name}`} download={item.file_name}
                  style={{ ...btn, display:'inline-block', textDecoration:'none', width:'auto', padding:'10px 24px' }}>
                  Download File ↓
                </a>
              </div>
            )}
          </div>
        )}

        {/* Additional Assets */}
        {item.assets && item.assets.length > 0 && (
          <div style={{ ...card, marginBottom:20 }}>
            <h2 style={{ color:'white', fontSize:17, fontWeight:700, margin:'0 0 16px' }}>📎 Additional Resources</h2>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {item.assets.map((asset, i) => {
                const assetEmbed = (() => {
                  if (!asset.url) return null;
                  const yt = asset.url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{11})/);
                  if (yt) return `https://www.youtube.com/embed/${yt[1]}?enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`;
                  const vi = asset.url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
                  if (vi) return `https://player.vimeo.com/video/${vi[1]}?autopause=1`;
                  return null;
                })();
                return (
                  <div key={asset.id || i} style={{ background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:12, overflow:'hidden' }}>
                    {/* Asset header */}
                    <div style={{ padding:'12px 16px', borderBottom: assetEmbed || (asset.stored_name && asset.folder) ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom: asset.description ? 6 : 0 }}>
                        <span style={{ background:'rgba(238,114,3,0.25)', border:'1px solid rgba(238,114,3,0.4)', borderRadius:4, color:'#f59e0b', fontSize:10, padding:'1px 7px', fontWeight:700, textTransform:'uppercase' }}>
                          {asset.asset_type}
                        </span>
                        <span style={{ color:'white', fontWeight:700, fontSize:14 }}>{asset.title || 'Asset ' + (i+1)}</span>
                        {asset.url && !assetEmbed && (
                          <a href={asset.url} target="_blank" rel="noopener noreferrer"
                            style={{ marginLeft:'auto', background:'rgba(238,114,3,0.2)', color:'#f59e0b', border:'none', borderRadius:6, padding:'4px 12px', fontSize:12, cursor:'pointer', textDecoration:'none', fontWeight:600 }}>
                            Open ↗
                          </a>
                        )}
                      </div>
                      {asset.description && <div style={{ color:'rgba(255,255,255,0.6)', fontSize:13, lineHeight:1.5 }}>{asset.description}</div>}
                    </div>
                    {/* Embedded content */}
                    {assetEmbed && (
                      <div style={{ position:'relative', width:'100%', paddingBottom:'56.25%' }}>
                        <iframe src={assetEmbed} style={{ width:'100%', height:'100%', border:'none', position:'absolute', top:0, left:0 }}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen title={asset.title} />
                      </div>
                    )}
                    {!assetEmbed && asset.stored_name && asset.folder && (
                      <div style={{ padding:'12px 16px' }}>
                        {asset.mime_type === 'application/pdf' ? (
                          <iframe src={`/api/files/${asset.folder}/${asset.stored_name}`}
                            style={{ width:'100%', height:400, border:'none', borderRadius:6 }} title={asset.title} />
                        ) : asset.mime_type?.startsWith('image/') ? (
                          <img src={`/api/files/${asset.folder}/${asset.stored_name}`} alt={asset.title}
                            style={{ maxWidth:'100%', borderRadius:6, display:'block' }} />
                        ) : (
                          <a href={`/api/files/${asset.folder}/${asset.stored_name}`} download={asset.file_name}
                            style={{ color:'#f59e0b', textDecoration:'none', fontSize:13 }}>
                            📄 {asset.file_name} — Download
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Feedback form */}
        {!submitted ? (
          <div style={{ ...card, marginBottom:20 }}>
            <h2 style={{ color:'white', fontSize:17, fontWeight:700, marginBottom:18, margin:'0 0 18px' }}>💬 Leave your feedback</h2>

            {item.first_name && <div style={{ color:'rgba(255,255,255,0.5)', fontSize:13, marginBottom:14 }}>
              Hi {item.first_name}! We'd love to hear your thoughts.
            </div>}

            {feedbackTypes.includes('score') && (
              <div style={{ marginBottom:18 }}>
                <div style={{ color:'rgba(255,255,255,0.7)', fontSize:13, fontWeight:600, marginBottom:6 }}>Your score</div>
                <StarRating value={form.score} onChange={v => setForm(p => ({ ...p, score: v }))} />
              </div>
            )}

            {feedbackTypes.includes('text') && (
              <div style={{ marginBottom:18 }}>
                <div style={{ color:'rgba(255,255,255,0.7)', fontSize:13, fontWeight:600, marginBottom:6 }}>
                  Your feedback{videoTimestamp !== null ? ` (timestamp: ${fmtTime(videoTimestamp)})` : ''}
                </div>
                <textarea style={{ ...inp, minHeight:100, resize:'vertical' }}
                  placeholder="Share your thoughts…"
                  value={form.feedback_text}
                  onChange={e => setForm(p => ({ ...p, feedback_text: e.target.value }))} />
              </div>
            )}

            {feedbackTypes.includes('file') && (
              <div style={{ marginBottom:18 }}>
                <div style={{ color:'rgba(255,255,255,0.7)', fontSize:13, fontWeight:600, marginBottom:6 }}>Attach a file (optional)</div>
                {attachedFile ? (
                  <div style={{ display:'flex', alignItems:'center', gap:10, background:'rgba(255,255,255,0.08)', borderRadius:8, padding:'10px 14px' }}>
                    <span style={{ color:'rgba(255,255,255,0.8)', fontSize:13, flex:1 }}>📎 {attachedFile.name}</span>
                    <button onClick={() => setAttachedFile(null)}
                      style={{ background:'none', border:'none', color:'rgba(255,255,255,0.4)', cursor:'pointer', fontSize:18, lineHeight:1 }}>✕</button>
                  </div>
                ) : (
                  <label style={{ display:'block', background:'rgba(255,255,255,0.05)', border:'2px dashed rgba(255,255,255,0.2)', borderRadius:8, padding:'20px', textAlign:'center', cursor:'pointer' }}>
                    <div style={{ color:'rgba(255,255,255,0.5)', fontSize:13 }}>Click to select a file</div>
                    <input type="file" style={{ display:'none' }} onChange={e => { if(e.target.files[0]) setAttachedFile(e.target.files[0]); }} />
                  </label>
                )}
              </div>
            )}

            {videoTimestamp !== null && !feedbackTypes.includes('text') && (
              <div style={{ marginBottom:18 }}>
                <div style={{ color:'rgba(255,255,255,0.5)', fontSize:12, background:'rgba(99,102,241,0.15)', border:'1px solid rgba(99,102,241,0.3)', borderRadius:8, padding:'8px 12px' }}>
                  ⏱ Video paused at <strong>{fmtTime(videoTimestamp)}</strong> — this timestamp will be included with your feedback
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div style={{ display:'flex', gap:12, marginTop:8, flexWrap:'wrap' }}>
              {/* Approve button — greyed if text entered */}
              <button
                style={{
                  flex:1, minWidth:160,
                  background: form.feedback_text.trim() ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                  color: form.feedback_text.trim() ? 'rgba(255,255,255,0.3)' : 'white',
                  border: form.feedback_text.trim() ? '1px solid rgba(255,255,255,0.15)' : 'none',
                  borderRadius:10, padding:'12px 20px', fontSize:14, fontWeight:700,
                  cursor: form.feedback_text.trim() ? 'not-allowed' : 'pointer',
                  transition:'all 0.2s', marginTop:0,
                  opacity: (submitting || uploadingFile) ? 0.6 : 1,
                }}
                onClick={() => !form.feedback_text.trim() && setShowApproveModal(true)}
                disabled={submitting || uploadingFile || !!form.feedback_text.trim()}>
                ✅ This is approved — no changes needed
              </button>

              {/* Send Feedback button */}
              <button
                style={{
                  flex:1, minWidth:140,
                  background:'linear-gradient(135deg, #EE7203 0%, #f59e0b 100%)',
                  color:'white', border:'none', borderRadius:10,
                  padding:'12px 20px', fontSize:14, fontWeight:700,
                  cursor:(submitting||uploadingFile)?'not-allowed':'pointer',
                  opacity:(submitting||uploadingFile)?0.7:1, marginTop:0,
                }}
                onClick={handleSubmit} disabled={submitting || uploadingFile}>
                {submitting ? 'Sending…' : '→ Send Feedback'}
              </button>
            </div>

            {form.feedback_text.trim() && (
              <div style={{ color:'rgba(255,255,255,0.35)', fontSize:11, marginTop:8 }}>
                ℹ Approve button is disabled when you have written feedback — use Send Feedback instead.
              </div>
            )}

            {/* Approval confirmation modal */}
            {showApproveModal && (
              <div style={{
                position:'fixed', inset:0, background:'rgba(0,0,0,0.7)',
                display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:20,
              }}>
                <div style={{
                  background:'#1e1b4b', border:'1px solid rgba(255,255,255,0.15)',
                  borderRadius:16, padding:28, maxWidth:440, width:'100%',
                }}>
                  <div style={{ fontSize:36, marginBottom:12, textAlign:'center' }}>✅</div>
                  <div style={{ color:'white', fontSize:18, fontWeight:800, marginBottom:12, textAlign:'center' }}>
                    Confirm Approval
                  </div>
                  <div style={{ color:'rgba(255,255,255,0.75)', fontSize:14, lineHeight:1.6, marginBottom:24, textAlign:'center' }}>
                    {approvalModalText}
                  </div>
                  <div style={{ display:'flex', gap:12 }}>
                    <button onClick={() => setShowApproveModal(false)}
                      style={{ flex:1, background:'rgba(255,255,255,0.1)', color:'rgba(255,255,255,0.7)', border:'1px solid rgba(255,255,255,0.2)', borderRadius:10, padding:'12px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
                      Cancel
                    </button>
                    <button onClick={handleApprove}
                      style={{ flex:1, background:'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', color:'white', border:'none', borderRadius:10, padding:'12px', fontSize:14, fontWeight:700, cursor:'pointer' }}>
                      Yes, I Approve
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ ...card, marginBottom:20, textAlign:'center' }}>
            <div style={{ fontSize:48, marginBottom:12 }}>✅</div>
            <div style={{ color:'white', fontSize:18, fontWeight:700 }}>Thank you!</div>
            <div style={{ color:'rgba(255,255,255,0.5)', marginTop:8 }}>Your response has been recorded.</div>
          </div>
        )}

        {/* View other responses (if allowed) */}
        {item.contacts_can_see_all_feedback && (
          <div style={{ marginBottom:20 }}>
            {!showResponses ? (
              <button onClick={loadResponses}
                style={{ ...btn, background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.2)' }}>
                View all feedback
              </button>
            ) : (
              <div style={card}>
                <h3 style={{ color:'white', fontWeight:700, marginBottom:14 }}>📋 All Feedback</h3>
                {responses.length === 0 && <div style={{ color:'rgba(255,255,255,0.4)' }}>No feedback yet.</div>}
                {responses.map((r, i) => (
                  <div key={i} style={{ borderTop: i>0 ? '1px solid rgba(255,255,255,0.1)' : 'none', paddingTop: i>0 ? 14 : 0, marginBottom:14 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                      <span style={{ color:'rgba(255,255,255,0.7)', fontWeight:600, fontSize:14 }}>
                        {r.respondent_name || 'Anonymous'}
                      </span>
                      {r.score && <span style={{ color:'#f59e0b' }}>{'★'.repeat(r.score)}{'☆'.repeat(5-r.score)}</span>}
                    </div>
                    {r.video_timestamp && <div style={{ color:'rgba(255,255,255,0.4)', fontSize:12, marginBottom:4 }}>⏱ {fmtTime(r.video_timestamp)}</div>}
                    {r.feedback_text && <div style={{ color:'rgba(255,255,255,0.8)', fontSize:14, lineHeight:1.6 }}>{r.feedback_text}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{ textAlign:'center', color:'rgba(255,255,255,0.25)', fontSize:11, paddingBottom:32 }}>
          Powered by <a href="https://www.ForgeBOS.com" target="_blank" rel="noopener noreferrer" style={{ color:'rgba(255,255,255,0.4)', textDecoration:'none' }}>ForgeBOS™</a> Feedback Preview
        </div>
      </div>
    </div>
  );
}
